'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Lightning,
  HardDrive,
  Calendar,
  ArrowRight,
  ArrowLeft,
  Clock,
  FileXls,
  CheckCircle,
  XCircle,
  Spinner
} from '@phosphor-icons/react';
import { useToastActions } from '../../components/ui/Toast';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { subscriptionApi } from '../../services/api';

interface UsageRecord {
  id: string;
  createdAt: string;
  type: 'workflow_execution' | 'storage_upload' | 'storage_delete';
  description: string;
  status: 'success' | 'failed' | 'pending';
  duration?: number;
  fileSize?: number;
}

interface UsageStats {
  currentMonthExecutions: number;
  maxWorkflowExecutions: number;
  currentStorageBytes: number;
  maxStorageBytes: number;
  executionUsagePercent: number;
  storageUsagePercent: number;
}



export function UsagePage() {
  const { t, i18n } = useTranslation();
  const { error: toastError } = useToastActions();
  const { fetchSubscription } = useSubscriptionStore();
  
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    loadData();
  }, [currentMonth]);

  const loadData = async () => {
    try {
      setLoading(true);
      await fetchSubscription();
      
      // 获取用量统计
      const usageResponse = await subscriptionApi.getUsageStats();
      if (usageResponse.code === 0 && usageResponse.data) {
        setUsageStats(usageResponse.data);
      }
      
      // 获取使用记录
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1; // API 使用 1-12
      const recordsResponse = await subscriptionApi.getUsageRecordsByMonth(year, month);
      if (recordsResponse.code === 0 && recordsResponse.data) {
        setUsageRecords((recordsResponse.data.records || []) as UsageRecord[]);
        void recordsResponse.data.total;
      }
    } catch (err) {
      console.error('Failed to load usage data:', err);
      toastError(t('usage.loadFailed') || '加载用量信息失败');
    } finally {
      setLoading(false);
    }
  };

  const formatStorage = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'zh' ? 'zh-CN' : i18n.language === 'ja' ? 'ja-JP' : 'en-US';
    return date.toLocaleDateString(locale) + ' ' + date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (nextMonth <= new Date()) {
      setCurrentMonth(nextMonth);
    }
  };

  const getMonthYearString = () => {
    const locale = i18n.language === 'zh' ? 'zh-CN' : i18n.language === 'ja' ? 'ja-JP' : 'en-US';
    return currentMonth.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
  };

  const isCurrentMonth = () => {
    const now = new Date();
    return currentMonth.getMonth() === now.getMonth() && currentMonth.getFullYear() === now.getFullYear();
  };

  const getRecordIcon = (type: string) => {
    switch (type) {
      case 'workflow_execution':
        return <FileXls className="w-5 h-5 text-blue-600" weight="fill" />;
      case 'storage_upload':
        return <HardDrive className="w-5 h-5 text-purple-600" weight="fill" />;
      case 'storage_delete':
        return <HardDrive className="w-5 h-5 text-red-600" weight="fill" />;
      default:
        return <FileXls className="w-5 h-5 text-zinc-600" weight="fill" />;
    }
  };

  const getRecordBgColor = (type: string) => {
    switch (type) {
      case 'workflow_execution':
        return 'bg-blue-50';
      case 'storage_upload':
        return 'bg-purple-50';
      case 'storage_delete':
        return 'bg-red-50';
      default:
        return 'bg-zinc-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl font-bold text-zinc-900">{t('usage.title') || '用量详情'}</h1>
          <p className="text-zinc-500 mt-1">{t('usage.subtitle') || '查看您的资源使用详情'}</p>
        </motion.div>

        {/* Usage Summary Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
        >
          {/* Executions */}
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Lightning className="w-5 h-5 text-blue-600" weight="fill" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">{t('usage.executions') || '表格流执行'}</p>
                  <p className="text-2xl font-bold text-zinc-900">
                    {usageStats?.currentMonthExecutions || 0}
                    <span className="text-sm font-normal text-zinc-400 ml-1">
                      / {usageStats?.maxWorkflowExecutions === -1 ? '∞' : (usageStats?.maxWorkflowExecutions || 0)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ 
                  width: `${Math.min(usageStats?.executionUsagePercent || 0, 100)}%`,
                  backgroundColor: (usageStats?.executionUsagePercent || 0) > 90 ? '#ef4444' : '#3b82f6'
                }}
              />
            </div>
            <p className="text-xs text-zinc-400 mt-2">
              {t('billing.usedPercent', { percent: Math.round(usageStats?.executionUsagePercent || 0) })}
            </p>
          </div>

          {/* Storage */}
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <HardDrive className="w-5 h-5 text-purple-600" weight="fill" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">{t('usage.storage') || '数据存储'}</p>
                  <p className="text-2xl font-bold text-zinc-900">
                    {formatStorage(usageStats?.currentStorageBytes || 0)}
                    <span className="text-sm font-normal text-zinc-400 ml-1">
                      / {usageStats?.maxStorageBytes === -1 ? '∞' : formatStorage(usageStats?.maxStorageBytes || 0)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-500 rounded-full transition-all"
                style={{ 
                  width: `${Math.min(usageStats?.storageUsagePercent || 0, 100)}%`,
                  backgroundColor: (usageStats?.storageUsagePercent || 0) > 90 ? '#ef4444' : '#a855f7'
                }}
              />
            </div>
            <p className="text-xs text-zinc-400 mt-2">
              {t('billing.usedPercent', { percent: Math.round(usageStats?.storageUsagePercent || 0) })}
            </p>
          </div>
        </motion.div>

        {/* Usage Records */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-zinc-200"
        >
          {/* Month Navigation */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-100">
            <h3 className="text-lg font-semibold text-zinc-900">{t('usage.records') || '使用记录'}</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevMonth}
                className="p-2 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-zinc-700 min-w-[100px] text-center">
                {getMonthYearString()}
              </span>
              <button
                onClick={handleNextMonth}
                disabled={isCurrentMonth()}
                className="p-2 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Records List */}
          <div className="divide-y divide-zinc-100">
            {usageRecords.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-zinc-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-zinc-300" />
                </div>
                <p className="text-zinc-500">{t('usage.noRecords') || '本月暂无使用记录'}</p>
              </div>
            ) : (
              usageRecords.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getRecordBgColor(record.type)}`}>
                      {getRecordIcon(record.type)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{record.description}</p>
                      <div className="flex items-center gap-2 text-xs text-zinc-400 mt-0.5">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(record.createdAt)}</span>
                        {record.duration !== undefined && record.duration > 0 && (
                          <>
                            <span>·</span>
                            <span>{formatDuration(record.duration)}</span>
                          </>
                        )}
                        {record.fileSize !== undefined && record.fileSize > 0 && (
                          <>
                            <span>·</span>
                            <span>{formatStorage(record.fileSize)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {record.status === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500" weight="fill" />
                    ) : record.status === 'failed' ? (
                      <XCircle className="w-5 h-5 text-red-500" weight="fill" />
                    ) : (
                      <Spinner className="w-5 h-5 text-blue-500 animate-spin" />
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default UsagePage;
